import { Image } from '@tiptap/extension-image';
import { TaskItem } from '@tiptap/extension-task-item';
import { TaskList } from '@tiptap/extension-task-list';
import { Placeholder } from '@tiptap/extension-placeholder';
import { Markdown } from '@tiptap/markdown';
import { EditorContent, useEditor, type Editor } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import { useEffect, useRef } from 'react';

import { insertAttachments } from './helpers';
import { RichTextToolbar } from './RichTextToolbar';
import type { RichTextEditorProps } from './types';

const CONTENT = [
  'max-h-64 min-h-[96px] overflow-y-auto px-2 py-1.5 text-sm text-control-foreground outline-none',
  '[&_p]:my-1 [&_h1]:my-2 [&_h1]:text-lg [&_h1]:font-semibold [&_h2]:my-2 [&_h2]:text-base [&_h2]:font-semibold [&_h3]:my-1.5 [&_h3]:font-semibold',
  '[&_ul]:my-1 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:my-1 [&_ol]:list-decimal [&_ol]:pl-5',
  '[&_ul[data-type=taskList]]:list-none [&_ul[data-type=taskList]]:pl-1 [&_li[data-type=taskItem]]:flex [&_li[data-type=taskItem]]:items-start [&_li[data-type=taskItem]]:gap-2 [&_li[data-type=taskItem]>label]:mt-0.5 [&_li[data-type=taskItem]>div]:flex-1',
  '[&_blockquote]:my-1 [&_blockquote]:border-l-2 [&_blockquote]:border-control-border [&_blockquote]:pl-3 [&_blockquote]:text-text-tertiary',
  '[&_code]:rounded [&_code]:bg-chip-hover [&_code]:px-1 [&_code]:font-mono [&_code]:text-xs [&_pre]:my-1 [&_pre]:rounded [&_pre]:bg-chip-hover [&_pre]:p-2 [&_pre_code]:bg-transparent [&_pre_code]:p-0',
  '[&_a]:text-focus [&_a]:underline [&_img]:inline-block [&_img]:max-h-40 [&_img]:rounded [&_img]:align-bottom',
  '[&_p.is-editor-empty:first-child]:before:pointer-events-none [&_p.is-editor-empty:first-child]:before:float-left [&_p.is-editor-empty:first-child]:before:h-0 [&_p.is-editor-empty:first-child]:before:text-input-placeholder [&_p.is-editor-empty:first-child]:before:content-[attr(data-placeholder)]',
].join(' ');

// Markdown in, markdown out: the value stays a plain string the way the rest of
// the app stores it, and ClickUp reads markdown on the other side.
export function RichTextEditor({
  value,
  ariaLabel,
  id,
  placeholder,
  onChange,
  attachments,
}: RichTextEditorProps) {
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const editorRef = useRef<Editor | null>(null);
  const attachRef = useRef<(files: File[]) => void>(() => undefined);

  // Dropped or pasted files arrive the same way as picked ones; without an
  // attachments prop they are left to the browser.
  const takeFiles = (files: FileList | undefined | null): boolean => {
    if (!attachments || !files || files.length === 0) return false;
    attachRef.current([...files]);
    return true;
  };

  const editor = useEditor({
    extensions: [
      // Markdown has no underline, so offering it would be a format that never
      // reaches ClickUp.
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        underline: false,
        // Attached files are addressed by blob URLs until they are uploaded.
        link: { openOnClick: false, autolink: true, protocols: ['blob'] },
      }),
      Image.configure({ inline: true }),
      TaskList,
      TaskItem.configure({ nested: true }),
      Placeholder.configure({ placeholder: placeholder ?? '' }),
      Markdown,
    ],
    content: value,
    contentType: 'markdown',
    shouldRerenderOnTransaction: true,
    editorProps: {
      attributes: {
        ...(id ? { id } : {}),
        role: 'textbox',
        'aria-multiline': 'true',
        'aria-label': ariaLabel,
        class: CONTENT,
      },
      // Text copied from a plain editor carries markdown as characters; read it
      // as markdown so lists, headings and dividers arrive formatted, as typing
      // them would.
      handlePaste: (_view, event) => {
        if (takeFiles(event.clipboardData?.files)) return true;
        const data = event.clipboardData;
        if (!data || data.types.includes('text/html')) return false;
        const text = data.getData('text/plain');
        if (!text.includes('\n')) return false;
        return (
          editorRef.current?.commands.insertContent(text, { contentType: 'markdown' }) ?? false
        );
      },
      handleDrop: (_view, event, _slice, moved) =>
        !moved && takeFiles((event as DragEvent).dataTransfer?.files),
    },
    onUpdate: ({ editor: e }) => onChangeRef.current(e.getMarkdown()),
  });

  editorRef.current = editor;

  useEffect(() => {
    if (editor && value !== editor.getMarkdown()) {
      editor.commands.setContent(value, { contentType: 'markdown', emitUpdate: false });
    }
  }, [editor, value]);

  attachRef.current = (files) => {
    const accepted = attachments?.onAttach(files) ?? [];
    if (editor && accepted.length > 0) insertAttachments(editor, accepted);
  };

  if (!editor) return null;

  return (
    <div className="w-full rounded-md border-2 border-transparent bg-control transition-colors focus-within:border-focus">
      <RichTextToolbar
        editor={editor}
        accept={attachments?.accept}
        onPickFiles={attachments ? (files) => attachRef.current(files) : undefined}
      />
      <EditorContent editor={editor} />
    </div>
  );
}
